import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "./stores/auth";
import { isNative } from "./lib/platform";

const routes = [
  { path: "/login", name: "login", component: () => import("./views/Login.vue"), meta: { public: true } },
  // Privacy policy : must be public so it can be linked from Google Play
  // Console (URL required) and from the login page itself.
  { path: "/privacy", name: "privacy", component: () => import("./views/Privacy.vue"), meta: { public: true } },
  // Account deletion landing — required by Google Play : provide a public
  // URL where users without the app can request deletion. v1.12.53+
  { path: "/account-deletion", name: "account-deletion", component: () => import("./views/AccountDeletion.vue"), meta: { public: true } },
  { path: "/", redirect: "/quotes" },
  { path: "/quotes", name: "quotes", component: () => import("./views/Quotes.vue") },
  { path: "/quotes/:id", name: "quote-editor", component: () => import("./views/QuoteEditor.vue"), props: true },
  { path: "/quotes/new", name: "quote-new", component: () => import("./views/QuoteEditor.vue") },
  // v1.12.57 — Vitres et Stats sont des entrées de prix manuelles +
  // analyse, indépendantes du scraper. Pas de meta requiresGpm.
  { path: "/parebrises", name: "price-history", component: () => import("./views/PriceHistory.vue") },
  { path: "/stats", name: "stats", component: () => import("./views/Stats.vue") },
  { path: "/settings", name: "settings", component: () => import("./views/Settings.vue") },
  // v1.12.56 — `requiresWeb` : page Plan/Billing interdite côté Android
  // pour respecter Google Play steering policy. Redirection vers /quotes
  // si l'user tape l'URL direct depuis l'app Android.
  { path: "/billing", name: "billing", component: () => import("./views/Billing.vue"), meta: { requiresWeb: true } },
  // Onboarding wizard for first-time supplier credential setup. The
  // post-login flow redirects new users here automatically. Existing
  // users can still access /settings → Comptes fournisseurs to manage
  // credentials directly.
  // v1.13.18 : route onboarding-credentials retirée (composant supprimé).
  // v1.13.2+ — Module Réclamations : suivi des dossiers de réclamation
  // d'assurance et de clients directs.
  { path: "/reclamations", name: "reclamations", component: () => import("./views/Reclamations.vue") },
  { path: "/reclamations/nouveau", name: "reclamation-new", component: () => import("./views/ReclamationCreate.vue") },
  { path: "/reclamations/:id", name: "reclamation-editor", component: () => import("./views/ReclamationEdit.vue"), props: true },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Handle stale chunk errors — when the user's cached index.html points to
// JS files that no longer exist after a new deploy. Reload the page to get
// the latest manifest.
router.onError((error, to) => {
  const isChunkLoadError =
    error.message?.includes("Failed to fetch dynamically imported module") ||
    error.message?.includes("Loading chunk") ||
    error.message?.includes("Loading CSS chunk");

  if (isChunkLoadError) {
    console.warn("Stale chunk detected, reloading to get fresh assets...", to.fullPath);
    // Use location.href to force a full page reload (not SPA navigation)
    window.location.href = to.fullPath;
  }
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  // Wait for auth state to load on first navigation
  while (auth.loading) {
    await new Promise((r) => setTimeout(r, 50));
  }
  if (!to.meta.public && !auth.isSignedIn) {
    return { name: "login", query: { redirect: to.fullPath } };
  }
  if (to.name === "login" && auth.isSignedIn) {
    return { name: "quotes" };
  }

  // v1.12.56 — Web-only guard. Si la route est interdite côté Android
  // (Google Play steering policy), redirection vers /quotes. Sur le
  // web, la route est accessible normalement.
  if (to.meta.requiresWeb && isNative()) {
    return { name: "quotes" };
  }
});
